import { useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { REFRESH_FLASH_MS, RefreshGenerationContext } from './refresh-flash';

interface RefreshFlashProps {
  /**
   * What the content shows, as a string: the content lights up when this
   * changes with a refresh. A signature of the card (its marks, its numbers),
   * never a timestamp the server rewrites without anything changing.
   */
  value: string;
  className?: string;
  children: ReactNode;
}

/**
 * Lights its content up for a second when a refresh changes it — the marks a
 * judge just put on an athlete's card, the round going to its corrections or
 * its check — so somebody reading the page sees what moved without comparing
 * it to memory.
 *
 * Never on the first draw: a page that has just opened has nothing to compare.
 */
export function RefreshFlash({ value, className, children }: RefreshFlashProps) {
  const generation = useContext(RefreshGenerationContext);
  const seen = useRef({ value, generation });
  // How many times it has been lit, and whether it is lit now. Apart, so the
  // second's timer belongs to the flash and not to whatever renders next.
  const [flashes, setFlashes] = useState(0);
  const [flashing, setFlashing] = useState(false);

  useEffect(() => {
    const previous = seen.current;
    seen.current = { value, generation };
    if (value !== previous.value && generation !== previous.generation) setFlashes((count) => count + 1);
  }, [value, generation]);

  useEffect(() => {
    if (flashes === 0) return;
    setFlashing(true);
    const timer = window.setTimeout(() => setFlashing(false), REFRESH_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [flashes]);

  return (
    <div
      className={cn(
        'rounded-md',
        flashing && 'animate-refresh-flash motion-reduce:animate-refresh-fade',
        className
      )}
      data-refresh-flash={flashing ? 'true' : undefined}
    >
      {children}
    </div>
  );
}
